import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Breadcrumbs,
  Button,
  DataTable,
  InputField,
  SearchWithFilter,
  Select,
  type DataTableColumn,
  type FilterSelection,
} from '@enamelai/design-system'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import { getAllProcedures } from '@services/api/procedure.api'
import {
  addFeeSchedule,
  checkContractIfExists,
  checkFeeScheduleIfExists,
  deleteFeeSchedule,
  getAllCarrierSettings,
  getAllFeeSchedule,
  updateFeeSchedule,
} from '@services/api/carrier.api'
import { getAllProviders } from '@services/api/provider.api'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createFeeScheduleUpdatedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { useAppSelector } from '@store/hooks'
import { formatAmount } from '@utils/numberFormatter.util'
import { logger } from '@utils/safeLogger'
import SettingsRowActions from '../../shared/SettingsRowActions.component'
import {
  sortRows,
  usePagedRows,
  useTableSort,
} from '../../shared/settingsTable'
import './FeeSchedulesStyle.css'

const LOADING_CONTAINER = '.fee-schedules-settings'

type LinkTo = 'clinic' | 'provider'
type FormMode = 'add' | 'edit' | 'view'

type CarrierRecord = {
  carrier_id: string
  carrier_name: string
  payer_id: string
}

type CarrierSetting = { carrier?: CarrierRecord | null }

type ProviderRecord = {
  provider_id: string
  first_name?: string
  last_name?: string
}

type FeeScheduleRecord = {
  fee_schedule_id: string
  description: string
  fees?: { procedure_id: string; allowed_amount?: string | number | null }[]
  carrier_fee_schedule?: {
    carrier_id?: string
    carrier?: CarrierRecord | null
    provider_id?: string | null
    provider?: ProviderRecord | null
    clinic_id?: string | null
    clinic?: { clinic_name?: string } | null
  } | null
}

type ProcedureRecord = {
  procedure_id: string
  procedure_code?: string
  description?: string
  procedure_settings?: { clinic_fee?: string | number | null } | null
}

type FeeScheduleForm = {
  link_to: LinkTo
  description: string
  carrier_id: string
  carrier_name: string
  payer_id: string
  provider_id: string
  provider_name: string
  clinic_id: string
}

type FormErrors = Partial<
  Record<'description' | 'carrier_id' | 'provider_id', string>
>

type FeeScheduleRow = {
  id: string
  title: string
  linkedTo: string
  name: string
  carrier: string
}

type ProcedureRow = {
  id: string
  code: string
  description: string
  clinicFee: number
  allowedAmount: string
  writeOff: number | null
}

const EMPTY_FORM: FeeScheduleForm = {
  link_to: 'clinic',
  description: '',
  carrier_id: '',
  carrier_name: '',
  payer_id: '',
  provider_id: '',
  provider_name: '',
  clinic_id: '',
}

const LINK_TO_OPTIONS = [
  { value: 'clinic', label: 'Clinic' },
  { value: 'provider', label: 'Provider' },
]

const FILTER_LABELS = { linkto: 'Linked to', carrier: 'Carrier' }
const EMPTY_FILTERS: FilterSelection = { linkto: [], carrier: [] }

const BREADCRUMB_LABEL: Record<FormMode, string> = {
  add: 'Add fee schedule',
  edit: 'Edit fee schedule',
  view: 'View fee schedule',
}

const providerName = (provider?: ProviderRecord | null): string =>
  provider ? `Dr. ${provider.first_name ?? ''} ${provider.last_name ?? ''}` : ''

const toAmountString = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const amount = Number(value)
  return Number.isNaN(amount) ? '' : String(Number(amount.toFixed(2)))
}

const isInvalidAmount = (value: string): boolean =>
  value.trim() !== '' && !(Number(value) > 0)

/** Zero-padded so the string sort in `sortRows` orders amounts numerically. */
const amountSortKey = (value: number | null): string =>
  value === null ? '' : value.toFixed(2).padStart(14, '0')

const FeeSchedules: React.FC = () => {
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()

  const [feeSchedules, setFeeSchedules] = useState<FeeScheduleRecord[]>([])
  const [carrierSettings, setCarrierSettings] = useState<CarrierSetting[]>([])
  const [providers, setProviders] = useState<ProviderRecord[]>([])
  const [procedures, setProcedures] = useState<ProcedureRecord[]>([])
  const [listLoading, setListLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<FilterSelection>(EMPTY_FILTERS)
  const [formMode, setFormMode] = useState<FormMode | null>(null)
  const [formData, setFormData] = useState<FeeScheduleForm>(EMPTY_FORM)
  const [allowedAmounts, setAllowedAmounts] = useState<Record<string, string>>(
    {},
  )
  const [errors, setErrors] = useState<FormErrors>({})
  const [amountErrors, setAmountErrors] = useState<Set<string>>(new Set())
  const [idToEdit, setIdToEdit] = useState<string | null>(null)
  const [rowToDelete, setRowToDelete] = useState<FeeScheduleRow | null>(null)
  const [listSort, toggleListSort] = useTableSort('name')
  const [procedureSort, toggleProcedureSort] = useTableSort()

  const isFormOpen = formMode !== null
  const isViewMode = formMode === 'view'

  useSettingsSubpageLoading(listLoading)

  const fetchData = useCallback(async (): Promise<void> => {
    try {
      const feeScheduleResponse = await getAllFeeSchedule()
      if (
        feeScheduleResponse.statusCode === 200 &&
        'fee_schedules' in feeScheduleResponse.data
      ) {
        setFeeSchedules(feeScheduleResponse.data.fee_schedules)
      }
      const carriersResponse = await getAllCarrierSettings()
      if (
        carriersResponse.statusCode === 200 &&
        'carrier_settings' in carriersResponse.data
      ) {
        setCarrierSettings(carriersResponse.data.carrier_settings)
      }
      const providerResponse = await getAllProviders()
      if (
        providerResponse.statusCode === 200 &&
        'providers' in providerResponse.data
      ) {
        setProviders(providerResponse.data.providers)
      }
    } catch (error) {
      logger.error('Error loading fee schedules:', error)
    } finally {
      setListLoading(false)
    }
  }, [])

  const resetForm = useCallback((): void => {
    setFormData(EMPTY_FORM)
    setAllowedAmounts({})
    setErrors({})
    setAmountErrors(new Set())
    setIdToEdit(null)
    setSearchQuery('')
  }, [])

  const closeForm = useCallback((): void => {
    resetForm()
    setFormMode(null)
  }, [resetForm])

  const gotoListPage = useCallback((): void => {
    closeForm()
    void fetchData()
  }, [closeForm, fetchData])

  useEffect(() => {
    gotoListPage()
  }, [gotoListPage, selectedClinic])

  const loadProcedures = useCallback(async (): Promise<void> => {
    try {
      const response = await getAllProcedures(true)
      if (response.statusCode === 200 && 'procedures' in response.data) {
        setProcedures(response.data.procedures as ProcedureRecord[])
      }
    } catch (error) {
      logger.error('Error loading procedures:', error)
    }
  }, [])

  const openAdd = useCallback(async (): Promise<void> => {
    resetForm()
    setFilters(EMPTY_FILTERS)
    await loadProcedures()
    setFormMode('add')
  }, [resetForm, loadProcedures])

  const openExisting = useCallback(
    async (id: string, mode: 'edit' | 'view'): Promise<void> => {
      const record = feeSchedules.find((item) => item.fee_schedule_id === id)
      if (!record) return
      resetForm()
      setFilters(EMPTY_FILTERS)
      await loadProcedures()
      const link = record.carrier_fee_schedule
      setIdToEdit(record.fee_schedule_id)
      setFormData({
        link_to: link?.provider_id ? 'provider' : 'clinic',
        description: record.description ?? '',
        carrier_id: link?.carrier_id ?? '',
        carrier_name: link?.carrier?.carrier_name ?? '',
        payer_id: link?.carrier?.payer_id ?? '',
        provider_id: link?.provider_id ?? '',
        provider_name: link?.provider_id ? providerName(link.provider) : '',
        clinic_id: link?.clinic_id ?? '',
      })
      setAllowedAmounts(
        Object.fromEntries(
          (record.fees ?? []).map((fee) => [
            fee.procedure_id,
            toAmountString(fee.allowed_amount),
          ]),
        ),
      )
      setFormMode(mode)
    },
    [feeSchedules, resetForm, loadProcedures],
  )

  const rows: FeeScheduleRow[] = useMemo(
    () =>
      feeSchedules.map((record) => {
        const link = record.carrier_fee_schedule
        const isProvider = !!link?.provider_id
        return {
          id: record.fee_schedule_id,
          title: record.description ?? '',
          linkedTo: isProvider ? 'Provider' : 'Clinic',
          name: isProvider
            ? providerName(link?.provider)
            : (link?.clinic?.clinic_name ?? ''),
          carrier: link?.carrier?.carrier_name ?? '',
        }
      }),
    [feeSchedules],
  )

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const linkFilter = filters.linkto ?? []
    const carrierFilter = filters.carrier ?? []
    return rows.filter((row) => {
      const matchesQuery =
        !query ||
        [row.title, row.linkedTo, row.name, row.carrier].some((field) =>
          field.toLowerCase().includes(query),
        )
      const matchesLink =
        linkFilter.length === 0 || linkFilter.includes(row.linkedTo)
      const matchesCarrier =
        carrierFilter.length === 0 || carrierFilter.includes(row.carrier)
      return matchesQuery && matchesLink && matchesCarrier
    })
  }, [rows, searchQuery, filters])

  const sortedRows = useMemo(
    () => sortRows(filteredRows, listSort),
    [filteredRows, listSort],
  )
  const { pageRows, pagination } = usePagedRows(sortedRows, {
    alwaysShow: true,
  })

  const carrierFilterOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.carrier).filter(Boolean))),
    [rows],
  )

  const procedureRows: ProcedureRow[] = useMemo(
    () =>
      procedures.map((procedure) => {
        const clinicFee = Number(procedure.procedure_settings?.clinic_fee ?? 0)
        const allowedAmount = allowedAmounts[procedure.procedure_id] ?? ''
        const allowed = Number(allowedAmount)
        return {
          id: procedure.procedure_id,
          code: procedure.procedure_code ?? '',
          description: procedure.description ?? '',
          clinicFee,
          allowedAmount,
          writeOff:
            allowedAmount.trim() !== '' && !Number.isNaN(allowed)
              ? clinicFee - allowed
              : null,
        }
      }),
    [procedures, allowedAmounts],
  )

  const filteredProcedureRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return procedureRows
    return procedureRows.filter((row) =>
      [row.code, row.description].some((field) =>
        field.toLowerCase().includes(query),
      ),
    )
  }, [procedureRows, searchQuery])

  const sortedProcedureRows = useMemo(
    () =>
      sortRows(filteredProcedureRows, procedureSort, (row, columnId) => {
        if (columnId === 'clinicFee') return amountSortKey(row.clinicFee)
        if (columnId === 'writeOff') return amountSortKey(row.writeOff)
        if (columnId === 'allowedAmount') {
          const allowed = Number(row.allowedAmount)
          return row.allowedAmount && !Number.isNaN(allowed)
            ? amountSortKey(allowed)
            : ''
        }
        return String(row[columnId as keyof ProcedureRow] ?? '').toLowerCase()
      }),
    [filteredProcedureRows, procedureSort],
  )
  const procedurePage = usePagedRows(sortedProcedureRows, { alwaysShow: true })

  const carrierOptions = useMemo(() => {
    const options = carrierSettings
      .map((setting) => setting.carrier)
      .filter((carrier): carrier is CarrierRecord => !!carrier?.carrier_id)
      .map((carrier) => ({
        value: carrier.carrier_id,
        label: carrier.carrier_name,
      }))
    if (
      formData.carrier_id &&
      !options.some((option) => option.value === formData.carrier_id)
    ) {
      options.unshift({
        value: formData.carrier_id,
        label: formData.carrier_name,
      })
    }
    return options
  }, [carrierSettings, formData.carrier_id, formData.carrier_name])

  const providerOptions = useMemo(() => {
    const options = providers.map((provider) => ({
      value: provider.provider_id,
      label: providerName(provider),
    }))
    if (
      formData.provider_id &&
      !options.some((option) => option.value === formData.provider_id)
    ) {
      options.unshift({
        value: formData.provider_id,
        label: formData.provider_name,
      })
    }
    return options
  }, [providers, formData.provider_id, formData.provider_name])

  const hasNoFeeSchedules = feeSchedules.length === 0

  const clearError = (field: keyof FormErrors): void => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev))
  }

  const handleCarrierChange = (carrierId: string): void => {
    const carrier = carrierSettings
      .map((setting) => setting.carrier)
      .find((item) => item?.carrier_id === carrierId)
    setFormData((prev) => ({
      ...prev,
      carrier_id: carrierId,
      carrier_name: carrier?.carrier_name ?? prev.carrier_name,
      payer_id: carrier?.payer_id ?? prev.payer_id,
    }))
    clearError('carrier_id')
  }

  const handleLinkToChange = (linkTo: LinkTo): void => {
    setFormData((prev) => ({
      ...prev,
      link_to: linkTo,
      ...(linkTo === 'clinic' ? { provider_id: '', provider_name: '' } : {}),
    }))
    clearError('provider_id')
  }

  const handleProviderChange = (providerId: string): void => {
    const provider = providers.find((item) => item.provider_id === providerId)
    setFormData((prev) => ({
      ...prev,
      provider_id: providerId,
      provider_name: providerName(provider),
    }))
    clearError('provider_id')
  }

  const handleAllowedAmountChange = (
    procedureId: string,
    value: string,
  ): void => {
    const next = value.replace(/[^\d.]/g, '')
    setAllowedAmounts((prev) => ({ ...prev, [procedureId]: next }))
    setAmountErrors((prev) => {
      if (!prev.has(procedureId)) return prev
      const updated = new Set(prev)
      updated.delete(procedureId)
      return updated
    })
  }

  const handleSave = useCallback(async (): Promise<void> => {
    const nextErrors: FormErrors = {}
    if (!formData.description.trim()) {
      nextErrors.description = 'Fee schedule name is required'
    }
    if (!formData.carrier_id) nextErrors.carrier_id = 'Carrier is required'
    if (formData.link_to === 'provider' && !formData.provider_id) {
      nextErrors.provider_id = 'Provider is required'
    }
    const invalidAmounts = new Set(
      Object.entries(allowedAmounts)
        .filter(([, value]) => isInvalidAmount(value))
        .map(([procedureId]) => procedureId),
    )
    setErrors(nextErrors)
    setAmountErrors(invalidAmounts)
    if (invalidAmounts.size > 0) {
      showToast(
        createFailedToast(
          'Allowed amount must be greater than 0',
          'Allowed amount must be greater than 0. Please try again.',
        ),
      )
    }
    if (Object.values(nextErrors).some(Boolean) || invalidAmounts.size > 0) {
      return
    }

    showLoading({ container: LOADING_CONTAINER })
    try {
      const feeScheduleExists = await checkFeeScheduleIfExists(
        formData.description,
        idToEdit ?? undefined,
      )
      if (feeScheduleExists?.data?.result?.exists) {
        setTimeout(() => {
          hideLoading(LOADING_CONTAINER)
          showToast(
            createFailedToast(
              'Fee schedule already exists',
              'Fee schedule already exists. Please try again with a different fee schedule.',
            ),
          )
        }, 2000)
        return
      }

      const contractExists = await checkContractIfExists({
        provider_id: formData.provider_id,
        carrier_id: formData.carrier_id,
        link_to: formData.link_to,
        ...(idToEdit && { fee_schedule_id: idToEdit }),
      })
      if (contractExists?.data?.result?.exists) {
        const owner = formData.link_to === 'provider' ? 'Provider' : 'Clinic'
        setTimeout(() => {
          hideLoading(LOADING_CONTAINER)
          showToast(
            createFailedToast(
              `${owner} contract for this carrier already exists`,
              `${owner} contract for this carrier already exists. Please update the existing contract or use a different carrier.`,
            ),
          )
        }, 2000)
        return
      }

      const payload = {
        ...formData,
        procedures: Object.entries(allowedAmounts)
          .filter(([, value]) => value.trim() !== '')
          .map(([procedureId, value]) => ({
            procedure_id: procedureId,
            allowed_amount: Number(value).toFixed(2),
          })),
      }
      const response = idToEdit
        ? await updateFeeSchedule(idToEdit, payload)
        : await addFeeSchedule(payload)

      setTimeout(() => {
        hideLoading(LOADING_CONTAINER)
        if (response.statusCode === 200) {
          showToast(createFeeScheduleUpdatedToast())
          gotoListPage()
        } else {
          showToast(
            createFailedToast(
              idToEdit
                ? 'Fee schedule updating failed'
                : 'Fee schedule adding failed',
              idToEdit
                ? 'Fee schedule updating failed. Please try again later.'
                : 'Fee schedule adding failed. Please try again later.',
            ),
          )
        }
      }, 2000)
    } catch (error) {
      logger.error('Error saving fee schedule:', error)
      hideLoading(LOADING_CONTAINER)
      showToast(
        createFailedToast(
          'Fee schedule save failed',
          'Please try again later.',
        ),
      )
    }
  }, [
    formData,
    allowedAmounts,
    idToEdit,
    showLoading,
    hideLoading,
    showToast,
    gotoListPage,
  ])

  const handleDeleteFeeSchedule = useCallback(async (): Promise<void> => {
    if (!rowToDelete) return
    const failedToast = createFailedToast(
      'Fee schedule not deleted',
      'Fee schedule not deleted. Please try again.',
    )
    showLoading({ container: LOADING_CONTAINER })
    try {
      const response = await deleteFeeSchedule(rowToDelete.id)
      setTimeout(() => {
        hideLoading(LOADING_CONTAINER)
        if (response.statusCode === 200) {
          showToast(
            createSuccessToast(
              'Fee schedule deleted successfully',
              'Fee schedule has been deleted successfully.',
            ),
          )
          gotoListPage()
        } else {
          showToast(failedToast)
        }
      }, 1000)
    } catch (e) {
      logger.error('Error deleting fee schedule:', e)
      setTimeout(() => {
        hideLoading(LOADING_CONTAINER)
        showToast(failedToast)
      }, 1000)
    } finally {
      setRowToDelete(null)
    }
  }, [rowToDelete, showLoading, hideLoading, showToast, gotoListPage])

  useEffect(() => {
    if (!formMode) return
    setHeaderTitle(
      <Breadcrumbs
        className="fs-breadcrumbs"
        showBackIcon
        onBackClick={closeForm}
        items={[
          { id: 'list', label: 'Fee schedule', onClick: closeForm },
          { id: 'current', label: BREADCRUMB_LABEL[formMode] },
        ]}
      />,
    )
    return () => setHeaderTitle(null)
  }, [formMode, closeForm, setHeaderTitle])

  useEffect(() => {
    setHeaderButtons(
      isFormOpen ? (
        <>
          <SearchWithFilter
            className="fs-search"
            placeholder="Search procedures"
            value={searchQuery}
            onChange={setSearchQuery}
            filters={{}}
            showFilter={false}
          />
          <Button
            className="settings-ds-button"
            variant="secondary"
            size="md"
            type="button"
            onClick={closeForm}
          >
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>
          {!isViewMode && (
            <Button
              className="settings-ds-button"
              variant="primary"
              size="md"
              type="button"
              onClick={() => {
                void handleSave()
              }}
            >
              Save changes
            </Button>
          )}
        </>
      ) : (
        <>
          <SearchWithFilter
            className="fs-search"
            placeholder="Search"
            value={searchQuery}
            onChange={setSearchQuery}
            filters={{
              linkto: ['Clinic', 'Provider'],
              carrier: carrierFilterOptions,
            }}
            filterLabels={FILTER_LABELS}
            selectedFilters={filters}
            onFilterChange={setFilters}
            showResetButton
            onReset={() => {
              setSearchQuery('')
              setFilters(EMPTY_FILTERS)
            }}
          />
          {!hasNoFeeSchedules && (
            <Button
              className="settings-ds-button"
              variant="primary"
              size="md"
              type="button"
              onClick={() => {
                void openAdd()
              }}
            >
              Add fee schedule
            </Button>
          )}
        </>
      ),
    )
    return () => setHeaderButtons(null)
  }, [
    isFormOpen,
    isViewMode,
    searchQuery,
    filters,
    carrierFilterOptions,
    hasNoFeeSchedules,
    closeForm,
    handleSave,
    openAdd,
    setHeaderButtons,
  ])

  const listColumns: DataTableColumn<FeeScheduleRow>[] = useMemo(
    () => [
      {
        id: 'title',
        label: 'Fee schedule title',
        sortable: true,
        showInfo: true,
      },
      { id: 'linkedTo', label: 'Linked to', sortable: true, showInfo: true },
      { id: 'name', label: 'Name', sortable: true, showInfo: true },
      { id: 'carrier', label: 'Carrier', sortable: true, showInfo: true },
    ],
    [],
  )

  const procedureColumns: DataTableColumn<ProcedureRow>[] = [
    { id: 'code', label: 'Code', sortable: true, showInfo: true, width: 104 },
    { id: 'description', label: 'Description', sortable: true },
    {
      id: 'clinicFee',
      label: 'Clinic fee',
      sortable: true,
      showInfo: true,
      width: 128,
      renderCell: (row) => formatAmount(row.clinicFee),
    },
    {
      id: 'allowedAmount',
      label: 'Allowed amt.',
      sortable: true,
      showInfo: true,
      width: 156,
      renderCell: (row) =>
        isViewMode ? (
          formatAmount(row.allowedAmount, '-')
        ) : (
          <InputField
            className="fs-allowed-input"
            variant="text"
            inputMode="decimal"
            aria-label={`Allowed amount for ${row.code}`}
            placeholder="0.00"
            clearable={false}
            fullWidth
            value={row.allowedAmount}
            error={amountErrors.has(row.id)}
            onChange={(e) => handleAllowedAmountChange(row.id, e.target.value)}
          />
        ),
    },
    {
      id: 'writeOff',
      label: 'Write-off',
      sortable: true,
      showInfo: true,
      width: 120,
      renderCell: (row) => formatAmount(row.writeOff, '-'),
    },
  ]

  const listState = listLoading
    ? 'loading'
    : filteredRows.length === 0
      ? 'empty'
      : 'default'

  return (
    <div className="fee-schedules-settings">
      {isFormOpen ? (
        <section className="fs-form">
          <h2 className="fs-section-title">Fee schedule details</h2>
          <div className="fs-form-row">
            <InputField
              fullWidth
              variant="text"
              label="Fee schedule name"
              placeholder="Enter fee schedule name"
              value={formData.description}
              disabled={isViewMode}
              error={!!errors.description}
              helperText={errors.description}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
                clearError('description')
              }}
            />
            <Select
              label="Carrier"
              placeholder="Select"
              options={carrierOptions}
              value={formData.carrier_id}
              disabled={isViewMode}
              error={!!errors.carrier_id}
              helperText={errors.carrier_id}
              onChange={(value) => handleCarrierChange(String(value))}
            />
            <Select
              label="Link to"
              placeholder="Select"
              options={LINK_TO_OPTIONS}
              value={formData.link_to}
              disabled={isViewMode}
              onChange={(value) => handleLinkToChange(value as LinkTo)}
            />
            {formData.link_to === 'provider' && (
              <Select
                label="Provider"
                placeholder="Select"
                options={providerOptions}
                value={formData.provider_id}
                disabled={isViewMode}
                error={!!errors.provider_id}
                helperText={errors.provider_id}
                onChange={(value) => handleProviderChange(String(value))}
              />
            )}
          </div>
          <DataTable<ProcedureRow>
            className="fs-procedures-table"
            columns={procedureColumns}
            rows={procedurePage.pageRows}
            state={filteredProcedureRows.length === 0 ? 'empty' : 'default'}
            emptyState={{
              title: 'No procedures found',
              description:
                procedureRows.length === 0
                  ? 'Add procedure codes first to set allowed amounts.'
                  : 'No procedures match your search.',
            }}
            sortColumnId={procedureSort.columnId}
            sortDirection={
              procedureSort.columnId ? procedureSort.direction : undefined
            }
            onSort={toggleProcedureSort}
            showActions={false}
            pagination={procedurePage.pagination}
          />
        </section>
      ) : (
        <DataTable<FeeScheduleRow>
          className="fs-table"
          columns={listColumns}
          rows={pageRows}
          state={listState}
          emptyState={{
            title: 'No fee schedules found',
            description: hasNoFeeSchedules
              ? "You don't have any fee schedules on record yet."
              : 'No fee schedules match your search.',
            actionLabel: hasNoFeeSchedules ? 'Add fee schedule' : undefined,
            onAction: hasNoFeeSchedules
              ? () => {
                  void openAdd()
                }
              : undefined,
          }}
          sortColumnId={listSort.columnId}
          sortDirection={listSort.columnId ? listSort.direction : undefined}
          onSort={toggleListSort}
          actionsLabel="Actions"
          renderActions={(row) => (
            <SettingsRowActions
              onView={() => {
                void openExisting(row.id, 'view')
              }}
              onEdit={() => {
                void openExisting(row.id, 'edit')
              }}
              onDelete={() => setRowToDelete(row)}
            />
          )}
          pagination={pagination}
        />
      )}
      <DeleteConfirmationModal
        show={!!rowToDelete}
        onHide={() => setRowToDelete(null)}
        onConfirm={() => {
          void handleDeleteFeeSchedule()
        }}
        entityName={rowToDelete?.title ?? ''}
        title="Delete fee schedule?"
        message="Are you sure you want to delete the fee schedule"
        confirmButtonText="Delete"
      />
    </div>
  )
}

export default FeeSchedules
